import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useAuth } from '../lib/auth'

export const Route = createFileRoute('/pricing')({
  component: PricingPage,
})

function PricingPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isChurned = user?.user_type === 'churned'
  
  const plans = [
    {
      name: 'FREE',
      price: '$0',
      period: 'forever',
      features: [
        '1,000 markets per page',
        '10 opportunities per day',
        '1 email alert',
        '7-day alert history',
        'Basic market search',
        '60-second refresh'
      ],
      limitations: [
        'No SMS alerts',
        'No data export',
        'No API access',
        'No trading'
      ],
      cta: 'CURRENT PLAN',
      disabled: true
    },
    {
      name: 'PRO',
      price: isChurned ? '$23' : '$29',
      originalPrice: isChurned ? '$29' : null,
      period: '/month',
      popular: true,
      features: [
        'Unlimited markets',
        'Unlimited opportunities',
        '50 email alerts',
        'SMS alerts included',
        'Advanced filters',
        '30-second refresh',
        'Data export (CSV)',
        'API access (1000/hr)',
        'Trading enabled',
        'Portfolio analytics',
        'Unlimited alert history',
        'Priority support'
      ],
      limitations: [],
      cta: 'UPGRADE TO PRO',
      disabled: false
    },
    {
      name: 'TEAM',
      price: '$99',
      period: '/month',
      features: [
        'Everything in Pro',
        '5 user accounts',
        'Shared alerts',
        'Webhook integration',
        'Custom API limits',
        'Team dashboard',
        'Priority support'
      ],
      limitations: [],
      cta: 'CONTACT SALES',
      disabled: false
    }
  ]
  
  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '1rem' }}>
          CHOOSE YOUR PLAN
        </h1>
        <p style={{ color: 'var(--sacred-text-dim)', fontSize: '1.1rem' }}>
          Unlock premium features and maximize your arbitrage opportunities
        </p>
        {isChurned && (
          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            backgroundColor: 'var(--sacred-success-bg)',
            color: 'var(--sacred-success)',
            borderRadius: '0.5rem',
            display: 'inline-block'
          }}>
            🎉 Welcome back! Get 20% off your first month
          </div>
        )}
      </div>
      
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '2rem',
        marginBottom: '3rem'
      }}>
        {plans.map((plan) => (
          <div
            key={plan.name}
            style={{
              backgroundColor: 'var(--sacred-bg-secondary)',
              border: plan.popular ? '2px solid var(--sacred-success)' : '1px solid var(--sacred-border)',
              borderRadius: '0.5rem',
              padding: '2rem',
              position: 'relative'
            }}
          >
            {plan.popular && (
              <div style={{
                position: 'absolute',
                top: '-1rem',
                right: '2rem',
                backgroundColor: 'var(--sacred-success)',
                color: 'var(--sacred-bg)',
                padding: '0.25rem 1rem',
                borderRadius: '0.25rem',
                fontSize: '0.8rem',
                fontWeight: 'bold'
              }}>
                MOST POPULAR
              </div>
            )}
            
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>
              {plan.name}
            </h2>
            
            <div style={{ marginBottom: '2rem' }}>
              {plan.originalPrice && (
                <span style={{
                  textDecoration: 'line-through',
                  color: 'var(--sacred-text-dim)',
                  marginRight: '0.5rem'
                }}>
                  {plan.originalPrice}
                </span>
              )}
              <span style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                {plan.price}
              </span>
              <span style={{ color: 'var(--sacred-text-dim)' }}>
                {plan.period}
              </span>
            </div>
            
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ 
                fontSize: '0.9rem', 
                marginBottom: '1rem',
                color: 'var(--sacred-text-dim)'
              }}>
                INCLUDES:
              </h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {plan.features.map((feature) => (
                  <li key={feature} style={{ marginBottom: '0.5rem' }}>
                    <span style={{ color: 'var(--sacred-success)', marginRight: '0.5rem' }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
              
              {plan.limitations.length > 0 && (
                <>
                  <h3 style={{ 
                    fontSize: '0.9rem', 
                    marginTop: '1rem',
                    marginBottom: '1rem',
                    color: 'var(--sacred-text-dim)'
                  }}>
                    NOT INCLUDED:
                  </h3>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {plan.limitations.map((limitation) => (
                      <li key={limitation} style={{ 
                        marginBottom: '0.5rem',
                        color: 'var(--sacred-text-dim)'
                      }}>
                        <span style={{ color: 'var(--sacred-error)', marginRight: '0.5rem' }}>✗</span>
                        {limitation}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            
            <button
              className={`action-btn ${plan.popular ? 'primary' : ''}`}
              disabled={plan.disabled}
              onClick={() => {
                if (plan.name === 'TEAM') {
                  window.location.href = 'mailto:sales@skybit.com?subject=Team Plan Inquiry'
                } else if (plan.name === 'PRO') {
                  // TODO: Integrate Stripe checkout
                  alert('Stripe integration coming soon!')
                }
              }}
              style={{
                width: '100%',
                padding: '1rem',
                fontSize: '0.9rem',
                opacity: plan.disabled ? 0.5 : 1,
                cursor: plan.disabled ? 'not-allowed' : 'pointer'
              }}
            >
              {plan.cta}
            </button>
          </div>
        ))}
      </div>
      
      <div style={{
        backgroundColor: 'var(--sacred-bg-secondary)',
        padding: '2rem',
        borderRadius: '0.5rem',
        marginBottom: '2rem'
      }}>
        <h2 style={{ fontSize: '1.3rem', marginBottom: '1rem' }}>
          FREQUENTLY ASKED QUESTIONS
        </h2>
        
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>
            Can I cancel anytime?
          </h3>
          <p style={{ color: 'var(--sacred-text-dim)' }}>
            Yes! Cancel your subscription anytime from your settings. No questions asked.
          </p>
        </div>
        
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>
            Do you offer refunds?
          </h3>
          <p style={{ color: 'var(--sacred-text-dim)' }}>
            We offer a 7-day money-back guarantee. If you're not satisfied, contact support for a full refund.
          </p>
        </div>
        
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>
            Can I change plans later?
          </h3>
          <p style={{ color: 'var(--sacred-text-dim)' }}>
            Absolutely! Upgrade or downgrade your plan anytime. Changes take effect on your next billing cycle.
          </p>
        </div>
        
        <div>
          <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>
            Do you offer discounts?
          </h3>
          <p style={{ color: 'var(--sacred-text-dim)' }}>
            Yes! Save 20% with annual billing. Students and non-profits get 50% off. Contact support for details.
          </p>
        </div>
      </div>
    </div>
  )
}